import { AtIcon, ChevronRightIcon, FlagIcon, PhoneIcon } from "@xola/icons";
import { Badge, Phone, Skeleton, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import React from "react";
import { Tag, TagId, Traveler } from "types";
import { isMinor } from "../../../models/User";
import { Currency } from "../../Common/Currency";
import { NoTranslate } from "../../Common/NoTranslate";
import { SkeletonLine } from "../../Common/Skeleton/TableSkeleton";
import { ContactData } from "./ContactData";
import { MergedRecordBadge } from "./MergedRecordBadge";
import { useSeller } from "@/hooks/useAccount";
import { useLocalizationStore } from "@/stores/localizationStore";

interface ContactItemProps {
    contact: Traveler;
    isExpanded: boolean;
    isActive: boolean;
    className?: string;
    onClick: (contact: Traveler) => void;
}

export const ContactItem = ({ contact, isExpanded = true, isActive = false, onClick, className }: ContactItemProps) => {
    const { currency } = useSeller();
    const locale = useLocalizationStore().getLocale();
    const handleClick = () => onClick(contact);

    return (
        <div
            className={clsx(
                "flex cursor-pointer items-center justify-between overflow-hidden border-b border-b-gray-light px-5 py-4.5 hover:bg-gray-lighter xl:px-10",
                isActive && "bg-blue-lighter",
                className,
            )}
            onClick={handleClick}
        >
            <div className="flex-shrink-0 space-y-1">
                <div className="space-x-2 truncate text-lg leading-p2 tracking-tightest">
                    {contact.isOrganizer && (
                        <Tooltip content="Main attendee">
                            <FlagIcon size="medium" />
                        </Tooltip>
                    )}
                    <NoTranslate className="font-bold" data-testid="customer-list-contact-name">
                        {contact.name}
                    </NoTranslate>
                    {isExpanded && <BadgeRenderer contact={contact} />}
                </div>

                <div className="flex items-center space-x-3 leading-p1 tracking-tightest text-gray-darker">
                    <div data-testid="customer-list-contact-email">
                        <AtIcon className="mr-3" />
                        {contact.email ? <var data-var="customerEmail">{contact.email}</var> : "No email provided"}
                    </div>
                    {isExpanded && contact.phone && (
                        <NoTranslate>
                            <PhoneIcon className="mr-1" />
                            <Phone>{contact.phone}</Phone>
                        </NoTranslate>
                    )}
                </div>

                {isExpanded && <ContactData contact={contact} />}
            </div>

            {isExpanded && (
                <div className="mr-2 space-x-6 text-lg font-bold">
                    <Currency.Split currency={currency} locale={locale}>
                        {contact.lifetimeValue ?? contact.value}
                    </Currency.Split>
                    <ChevronRightIcon />
                </div>
            )}
        </div>
    );
};

const BadgeRenderer = ({ contact }: { contact: Traveler }) => {
    const { isOrganizer } = contact;
    const isWaitlist = contact.tags?.some((tag: Tag) => tag.id === TagId.WAITLIST);
    const isContactMinor = contact.data?.["Date of birth"] && isMinor(contact.data?.["Date of birth"]);

    return (
        <span className="space-x-2">
            {isOrganizer && (
                <Badge color="secondary" size="small">
                    <span>Organizer</span>
                </Badge>
            )}
            {isWaitlist && (
                <Badge color="secondary" size="small">
                    <span>Waitlist Only</span>
                </Badge>
            )}
            {isContactMinor && (
                <Badge color="secondary" size="small">
                    <span>Minor</span>
                </Badge>
            )}
            <MergedRecordBadge contact={contact} />
        </span>
    );
};

export const ContactItemSkeleton = () => {
    return (
        <Skeleton
            height={87}
            classNames={{
                container:
                    "!bg-white px-4 !w-full !justify-start border-0 border-b border-b-gray-lighter px-5 py-4.5 xl:px-10",
                text: "!w-full",
            }}
        >
            <div className="flex w-full items-center justify-between">
                <div className="space-y-1">
                    <div className="flex items-center space-x-2 text-lg leading-p2 tracking-tightest">
                        <FlagIcon size="medium" />
                        <SkeletonLine className="inline-block w-44" />
                        <Badge color="secondary" size="small" className="text-gray">
                            <span>Organizer</span>
                        </Badge>
                    </div>

                    <div className="flex items-center space-x-3 leading-p1 tracking-tightest">
                        <NoTranslate>
                            <AtIcon className="mr-3" />
                            customer@email.com
                        </NoTranslate>
                    </div>
                </div>
                <div className="mr-2 space-x-6 text-lg font-bold">
                    <Currency>100</Currency>
                    <span>
                        <ChevronRightIcon />
                    </span>
                </div>
            </div>
        </Skeleton>
    );
};
